/* eslint-disable sonarjs/no-identical-functions */
import { createAction, createReducer } from '@reduxjs/toolkit';
import { getSavedAdmitadId } from 'lib/admitad';
import { SubscriptionBuyAPICall } from 'reactApp/api/billing/subscription/SubscriptionBuyAPICall';
import { SubscriptionNoRenewAPICall } from 'reactApp/api/billing/subscription/SubscriptionNoRenewAPICall';
import { SubscriptionRenewAPICall } from 'reactApp/api/billing/subscription/SubscriptionRenewAPICall';
import { SubscriptionAutoRenewAPICall } from 'reactApp/api/billing/subscription/SubscriptionRenewByBindIdAPICall';
import { getQueryParams, getUtmSourceString } from 'reactApp/appHelpers/settingsHelpers';
import {
    BillingBuyTemplates,
    BillingBuyViewTemplates,
    BuyApiParams,
    IAddAutoRenewAction,
    IBuySubscription,
    IOpenCancelMailSubsDialogAction,
    IRenewSubscription,
    ITrialSubscription,
    RenewApiParams,
} from 'reactApp/modules/buySubscription/buySubscription.types';
import { AppDispatch, RootState } from 'reactApp/store';

const INITIAL_STATE = {
    isLoading: false,
    isLoaded: false,
    hasError: false,
    link: '',
    renewLink: '',
};

const startLoading = createAction('@buySubscription/startLoading');
const hasError = createAction('@buySubscription/hasError');
const isLoaded = createAction('@buySubscription/isLoaded');
const resetLink = createAction('@buySubscription/resetLink');
const setRenewLink = createAction<{ renewLink: string }>('@buySubscription/setRenewLink');
const setLink = createAction<{ link: string }>('@buySubscription/setLink');
const openCancelAutorenewMailSubsDialog = createAction<IOpenCancelMailSubsDialogAction>(
    '@buySubscription/openCancelAutorenewMailSubsDialog'
);
const addAutorenewToSubscription = createAction<IAddAutoRenewAction>('@buySubscription/addAutorenewToSubscription');
const buyTrialSubscription = createAction<ITrialSubscription>('@buySubscription/buyTrialSubscription');

const buySubscriptionReducer = createReducer(INITIAL_STATE, {
    [resetLink.type]: () => INITIAL_STATE,
    [startLoading.type]: () => ({
        ...INITIAL_STATE,
        isLoading: true,
    }),
    [hasError.type]: () => ({
        ...INITIAL_STATE,
        hasError: true,
    }),
    [isLoaded.type]: () => ({
        ...INITIAL_STATE,
        isLoaded: true,
    }),
    [setRenewLink.type]: (_, action) => ({
        ...INITIAL_STATE,
        isLoaded: true,
        renewLink: action.payload.renewLink,
    }),
    [setLink.type]: (_, action) => ({
        ...INITIAL_STATE,
        isLoaded: true,
        link: action.payload.link,
    }),
});

const renewSubscription =
    ({ id }: IRenewSubscription) =>
    (dispatch: AppDispatch) => {
        const api = new SubscriptionRenewAPICall();

        dispatch(startLoading());

        return new Promise((resolve, reject) => {
            const params: RenewApiParams = {
                sub_id: id,
                skin: BillingBuyTemplates.CHECKOUT_VKPAY,
                view: BillingBuyViewTemplates.checkout,
            };

            api.makeRequest(params)
                .then(({ data }) =>
                    resolve(
                        dispatch(
                            setRenewLink({
                                renewLink: data.pw,
                            })
                        )
                    )
                )
                .catch((error) => {
                    dispatch(hasError());

                    return reject(error);
                });
        });
    };

const buySubscription =
    ({ id, isMobile, payMethod = '' }: IBuySubscription) =>
    (dispatch: AppDispatch) => {
        const api = new SubscriptionBuyAPICall();

        dispatch(startLoading());

        return new Promise((resolve, reject) => {
            const admitadId = getSavedAdmitadId();
            const { partner } = getQueryParams();

            const params: BuyApiParams = {
                product_id: id,
                admitad_uid: admitadId,
                skin: isMobile ? BillingBuyTemplates.CLOUD_B2C_MOBILE : BillingBuyTemplates.CHECKOUT_VKPAY,
                view: BillingBuyViewTemplates.checkout,
                source: getUtmSourceString(),
            };

            if (partner) {
                params.partner = partner;
            }

            if (payMethod) {
                params.pay_method = payMethod;
            }

            api.makeRequest(params)
                .then(({ data }) =>
                    resolve(
                        dispatch(
                            setLink({
                                link: data.pw,
                            })
                        )
                    )
                )
                .catch((error) => {
                    dispatch(hasError());

                    return reject(error);
                });
        });
    };

const cancelAutorenew = (id: string) => (dispatch: AppDispatch) => {
    const api = new SubscriptionNoRenewAPICall();

    dispatch(startLoading());

    return new Promise((resolve, reject) => {
        api.makeRequest({
            sub_id: id,
        })
            .then(() => resolve(dispatch(isLoaded())))
            .catch((error) => {
                dispatch(hasError());

                return reject(error);
            });
    });
};

const addAutorenew = (id: string) => (dispatch: AppDispatch) => {
    const api = new SubscriptionAutoRenewAPICall();

    dispatch(startLoading());

    return new Promise((resolve, reject) => {
        api.makeRequest({
            sub_id: id,
        })
            .then(() => resolve(dispatch(isLoaded())))
            .catch((error) => {
                dispatch(hasError());

                reject(error);
            });
    });
};

const getLink = (state: RootState) => state.buySubscription;

export const BuySubscriptionActions = {
    startLoading,
    hasError,
    buySubscription,
    cancelAutorenew,
    addAutorenew,
    setLink,
    resetLink,
    renewSubscription,
    buyTrialSubscription,
    openCancelAutorenewMailSubsDialog,
    addAutorenewToSubscription,
};

export const BuySubscriptionSelectors = {
    getLink,
};

export { buySubscriptionReducer };
